import { useState } from 'react';

import { GroupedOption } from '@/components/UI/Fields/ReactFormFields/CreatableSelectField';
import useMaterieelTypen from '@/state/selectors/useMaterieelTypen';
import { materieelsoorten } from '@/utils/materieelsoorten';

export const useMaterieelTypenSearch = (initialValueMaterieelTypen: string[] = []) => {
  // Create an option object for the select component
  const createOption = (label: string) => ({
    label,
    value: label,
  });

  const [selectedMaterieelTypen, setSelectedMaterieelTypen] = useState<string[]>(initialValueMaterieelTypen);

  const onbIntercity1 = [
    'Alle ONB Intercity 1 toevoegen',
    'ICM-3',
    'ICM-4',
    'DDZ-4',
    'DDZ-6',
    'ICNG-5',
    'ICNG-8',
    'ICNG-8B',
  ];
  const sprinters = ['Alle sprinters toevoegen', ...materieelsoorten.sprinters];
  const intercities = ['Alle intercities toevoegen', ...materieelsoorten.intercities];

  const pcBrussel = ['Alle PC Brussel toevoegen', 'SWB6-25KV-2+6', 'ICNG-8B'];
  const pcDuitsland = [
    'Alle PC Duitsland toevoegen',
    'NEO-ICE3',
    'E-LOC-VECT',
    'DB-BER9-9',
    'DB-B5-5',
    'DB-B6-6',
    'DB-B7-7',
    'DB-B8-8',
    'ICNG-8D',
  ];
  const intVolledig = ['Alle INT volledig toevoegen', ...materieelsoorten.internationaal];
  const onbSprinter1 = ['Alle ONB Sprinter 1 toevoegen', 'SNG-3', 'SNG-4', 'SNG-3T', 'SNG-4T', 'FLIRT TAG-2'];
  const onbSprinter2 = ['Alle ONB Sprinter 2 toevoegen', 'SLT-4', 'SLT-6', 'SLT-6S', 'FLIRT FFF-3', 'FLIRT FFF-4'];
  const onbIntercity2 = ['Alle ONB Intercity 2 toevoegen', 'VIRM-4', 'VIRM-6'];
  const pcEurostar = ['Alle PC Eurostar toevoegen', 'TGV-PBA', 'EUR-PBA'];

  const sprinterOptions = [...sprinters.map((item) => createOption(item))];
  const intercityOptions = [...intercities.map((item) => createOption(item))];
  const onbSprinter1Options = [...onbSprinter1.map((item) => createOption(item))];
  const onbSprinter2Options = [...onbSprinter2.map((item) => createOption(item))];
  const onbIntercity1Options = [...onbIntercity1.map((item) => createOption(item))];
  const onbIntercity2Options = [...onbIntercity2.map((item) => createOption(item))];
  const pcBrusselOptions = [...pcBrussel.map((item) => createOption(item))];
  const pcDuitslandOptions = [...pcDuitsland.map((item) => createOption(item))];
  const pcEurostarOptions = [...pcEurostar.map((item) => createOption(item))];
  const intVolledigOptions = [...intVolledig.map((item) => createOption(item))];

  const lijstVanMatTypen = useMaterieelTypen();

  const handleMaterieelTypenChange = (selectedOptions) => {
    // Normaliseer waardes naar hoofdletter materieeltypes en trim begin en eind whitespace
    const normalizedOptions = selectedOptions
      .filter((item) => item !== null && item !== undefined)
      .map((item) => item.trim().toUpperCase())
      .filter((item) => item.length >= 1);

    let allMatchedMaterieelTypen: string[] = [];

    // Bepaal materieeltypen behorend bij de geselecteerde (shortcut/wildcard) optie(s)
    normalizedOptions.forEach((option) => {
      let typenCorrespondingToOption: string[] = [];
      if (option.toUpperCase() === 'ALLE SPRINTERS TOEVOEGEN') {
        typenCorrespondingToOption = sprinters.filter((item) => item.toLowerCase() !== 'alle sprinters toevoegen');
      } else if (option.toUpperCase() === 'ALLE INTERCITIES TOEVOEGEN') {
        typenCorrespondingToOption = intercities.filter((item) => item.toLowerCase() !== 'alle intercities toevoegen');
      } else if (option.toUpperCase() === 'ALLE ONB SPRINTER 1 TOEVOEGEN') {
        typenCorrespondingToOption = onbSprinter1.filter(
          (item) => item.toLowerCase() !== 'alle onb sprinter 1 toevoegen',
        );
      } else if (option.toUpperCase() === 'ALLE ONB SPRINTER 2 TOEVOEGEN') {
        typenCorrespondingToOption = onbSprinter2.filter(
          (item) => item.toLowerCase() !== 'alle onb sprinter 2 toevoegen',
        );
      } else if (option.toUpperCase() === 'ALLE ONB INTERCITY 1 TOEVOEGEN') {
        typenCorrespondingToOption = onbIntercity1.filter(
          (item) => item.toLowerCase() !== 'alle onb intercity 1 toevoegen',
        );
      } else if (option.toUpperCase() === 'ALLE ONB INTERCITY 2 TOEVOEGEN') {
        typenCorrespondingToOption = onbIntercity2.filter(
          (item) => item.toLowerCase() !== 'alle onb intercity 2 toevoegen',
        );
      } else if (option.toUpperCase() === 'ALLE PC BRUSSEL TOEVOEGEN') {
        typenCorrespondingToOption = pcBrussel.filter((item) => item.toLowerCase() !== 'alle pc brussel toevoegen');
      } else if (option.toUpperCase() === 'ALLE PC DUITSLAND TOEVOEGEN') {
        typenCorrespondingToOption = pcDuitsland.filter((item) => item.toLowerCase() !== 'alle pc duitsland toevoegen');
      } else if (option.toUpperCase() === 'ALLE PC EUROSTAR TOEVOEGEN') {
        typenCorrespondingToOption = pcEurostar.filter((item) => item.toLowerCase() !== 'alle pc eurostar toevoegen');
      } else if (option.toUpperCase() === 'ALLE INT VOLLEDIG TOEVOEGEN') {
        typenCorrespondingToOption = intVolledig.filter((item) => item.toLowerCase() !== 'alle int volledig toevoegen');
      } else if (option.endsWith('*')) {
        // Handle wildcard search
        const prefix = option.slice(0, -1); // Remove the star at the end
        typenCorrespondingToOption = lijstVanMatTypen.filter((item) => item.startsWith(prefix));
      } else if (!allMatchedMaterieelTypen.includes(option)) {
        typenCorrespondingToOption = [option];
      }
      allMatchedMaterieelTypen = [
        ...allMatchedMaterieelTypen,
        ...typenCorrespondingToOption.filter((item) => !allMatchedMaterieelTypen.includes(item)),
      ];
    });

    // Set geselecteerde materieeltypes
    if (
      normalizedOptions.some(
        (option) =>
          option.endsWith('*') ||
          option.toUpperCase() === 'ALLE SPRINTERS TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE INTERCITIES TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE ONB HSL TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE ONB SPRINTERS 1 TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE ONB SPRINTERS 2 TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE ONB INTERCITY 1 TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE ONB INTERCITY 2 TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE PC BRUSSEL TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE PC DUITSLAND TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE PC EUROSTAR TOEVOEGEN' ||
          option.toUpperCase() === 'ALLE INT VOLLEDIG TOEVOEGEN',
      )
    ) {
      setSelectedMaterieelTypen((prevSelected) => [
        ...prevSelected,
        ...allMatchedMaterieelTypen.filter((item) => !prevSelected.includes(item)),
      ]);
    } else {
      setSelectedMaterieelTypen(allMatchedMaterieelTypen);
    }
  };

  const excludeItems = [
    ...sprinters,
    ...intercities,
    ...onbSprinter1,
    ...onbSprinter2,
    ...onbIntercity1,
    ...onbIntercity2,
    ...pcBrussel,
    ...pcDuitsland,
    ...pcEurostar,
    ...intVolledig,
  ];

  const listWithoutPredefinedCategories = lijstVanMatTypen
    .filter((item) => !excludeItems.includes(item))
    .map((item) => createOption(item));

  const groupedOptions: GroupedOption[] = [
    {
      label: 'Sprinters',
      options: sprinterOptions,
    },
    {
      label: 'Intercities',
      options: intercityOptions,
    },
    {
      label: 'ONB Sprinter 1',
      options: onbSprinter1Options,
    },
    {
      label: 'ONB Sprinter 2',
      options: onbSprinter2Options,
    },
    {
      label: 'ONB Intercity 1',
      options: onbIntercity1Options,
    },
    {
      label: 'ONB Intercity 2',
      options: onbIntercity2Options,
    },
    {
      label: 'PC Brussel',
      options: pcBrusselOptions,
    },
    {
      label: 'PC Duitsland',
      options: pcDuitslandOptions,
    },
    {
      label: 'PC Eurostar',
      options: pcEurostarOptions,
    },
    {
      label: 'INT Volledig',
      options: intVolledigOptions,
    },
    {
      label: 'Overige',
      options: listWithoutPredefinedCategories,
    },
  ];

  return {
    groupedOptions,
    handleMaterieelTypenChange,
    createOption,
    selectedMaterieelTypen,
    setSelectedMaterieelTypen,
  };
};
